import React from "react";
import { Link } from "react-router-dom";
import { AnimatedSection } from "../components/IntersectionObserver.tsx";
import { ArrowLeft } from "lucide-react";
import Logo from "../components/Logo.tsx";

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="legal-page">
      {/* Header */}
      <header className="legal-header">
        <div className="dark-container">
          <div className="legal-header-content">
            <Logo size="medium" />
            <Link to="/" className="back-button">
              <ArrowLeft size={20} />
              <span>На главную</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="legal-content">
        <div className="dark-container">
          <AnimatedSection>
            <div className="legal-document">
              <h1 className="display-medium">Политика конфиденциальности</h1>
              <p className="body-medium legal-updated">Последнее обновление: {new Date().toLocaleDateString('ru-RU', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

              <section className="legal-section">
                <h2 className="heading-2">1. Общие положения</h2>
                <p className="body-medium">
                  Настоящая Политика конфиденциальности определяет порядок обработки и защиты персональных данных пользователей сайта AGK Commerce (далее — «Сайт»). Используя наш Сайт, вы соглашаетесь с условиями данной Политики конфиденциальности.
                </p>
                <p className="body-medium">
                  Мы уважаем вашу конфиденциальность и обязуемся защищать ваши персональные данные. Данная политика объясняет, как мы собираем, используем и защищаем вашу информацию.
                </p>
              </section>

              <section className="legal-section">
                <h2 className="heading-2">2. Сбор информации</h2>
                <p className="body-medium">Мы можем собирать следующие виды персональных данных:</p>
                <ul className="body-medium legal-list">
                  <li><strong>Контактная информация:</strong> имя, адрес электронной почты, номер телефона, название компании</li>
                  <li><strong>Техническая информация:</strong> IP-адрес, тип браузера, операционная система, время посещения</li>
                  <li><strong>Данные о взаимодействии:</strong> информация о том, как вы используете наш Сайт, какие страницы посещаете</li>
                  <li><strong>Коммерческая информация:</strong> данные о проектах, бюджете, предпочтениях</li>
                </ul>
              </section>

              <section className="legal-section">
                <h2 className="heading-2">3. Цели обработки данных</h2>
                <p className="body-medium">Мы используем собранные данные для следующих целей:</p>
                <ul className="body-medium legal-list">
                  <li>Обработка и ответ на ваши запросы через контактную форму</li>
                  <li>Предоставление информации о наших услугах и продуктах</li>
                  <li>Улучшение качества обслуживания и пользовательского опыта</li>
                  <li>Анализ использования Сайта и оптимизация его работы</li>
                  <li>Отправка маркетинговых материалов (с вашего согласия)</li>
                  <li>Соблюдение законодательных требований</li>
                </ul>
              </section>

              <section className="legal-section">
                <h2 className="heading-2">4. Правовые основания обработки</h2>
                <p className="body-medium">Обработка ваших персональных данных осуществляется на основании:</p>
                <ul className="body-medium legal-list">
                  <li>Вашего согласия на обработку персональных данных</li>
                  <li>Необходимости исполнения договора, стороной которого вы являетесь</li>
                  <li>Наших законных интересов (улучшение услуг, безопасность)</li>
                  <li>Требований применимого законодательства</li>
                </ul>
              </section>

              <section className="legal-section">
                <h2 className="heading-2">5. Передача данных третьим лицам</h2>
                <p className="body-medium">
                  Мы не продаем и не передаем ваши персональные данные третьим лицам, за исключением следующих случаев:
                </p>
                <ul className="body-medium legal-list">
                  <li><strong>Сервис-провайдеры:</strong> мы можем передавать данные доверенным партнерам, которые помогают нам в предоставлении услуг (хостинг, аналитика, email-рассылки)</li>
                  <li><strong>Юридические требования:</strong> когда раскрытие информации требуется по закону или в рамках судебного процесса</li>
                  <li><strong>Защита прав:</strong> для защиты наших прав, собственности или безопасности</li>
                  <li><strong>С вашего согласия:</strong> в других случаях с вашего явного согласия</li>
                </ul>
              </section>

              <section className="legal-section">
                <h2 className="heading-2">6. Безопасность данных</h2>
                <p className="body-medium">
                  Мы применяем современные технические и организационные меры для защиты ваших данных от несанкционированного доступа, изменения, раскрытия или уничтожения:
                </p>
                <ul className="body-medium legal-list">
                  <li>Шифрование данных при передаче (SSL/TLS)</li>
                  <li>Ограничение доступа к персональным данным только уполномоченным сотрудникам</li>
                  <li>Регулярное обновление систем безопасности</li>
                  <li>Защищенные серверы и базы данных</li>
                  <li>Регулярные аудиты безопасности</li>
                </ul>
              </section>

              <section className="legal-section">
                <h2 className="heading-2">7. Использование cookies</h2>
                <p className="body-medium">
                  Наш Сайт использует файлы cookie для улучшения пользовательского опыта. Cookies — это небольшие текстовые файлы, которые хранятся на вашем устройстве.
                </p>
                <p className="body-medium">Мы используем следующие типы cookies:</p>
                <ul className="body-medium legal-list">
                  <li><strong>Необходимые cookies:</strong> обеспечивают базовую функциональность сайта</li>
                  <li><strong>Аналитические cookies:</strong> помогают нам понять, как посетители используют наш сайт</li>
                  <li><strong>Функциональные cookies:</strong> запоминают ваши предпочтения</li>
                </ul>
                <p className="body-medium">
                  Вы можете настроить или отключить cookies в настройках вашего браузера.
                </p>
              </section>

              <section className="legal-section">
                <h2 className="heading-2">8. Ваши права</h2>
                <p className="body-medium">В соответствии с применимым законодательством, вы имеете следующие права:</p>
                <ul className="body-medium legal-list">
                  <li><strong>Право на доступ:</strong> получить информацию о том, какие данные мы о вас храним</li>
                  <li><strong>Право на исправление:</strong> запросить исправление неточных данных</li>
                  <li><strong>Право на удаление:</strong> запросить удаление ваших персональных данных</li>
                  <li><strong>Право на ограничение обработки:</strong> ограничить обработку ваших данных в определенных случаях</li>
                  <li><strong>Право на переносимость:</strong> получить ваши данные в структурированном формате</li>
                  <li><strong>Право на возражение:</strong> возразить против обработки ваших данных</li>
                  <li><strong>Право на отзыв согласия:</strong> отозвать ранее данное согласие в любое время</li>
                </ul>
              </section>

              <section className="legal-section">
                <h2 className="heading-2">9. Хранение данных</h2>
                <p className="body-medium">
                  Мы храним ваши персональные данные только в течение срока, необходимого для достижения целей, указанных в данной Политике, или в соответствии с требованиями законодательства.
                </p>
                <p className="body-medium">
                  После окончания срока хранения данные безопасно удаляются или анонимизируются.
                </p>
              </section>

              <section className="legal-section">
                <h2 className="heading-2">10. Международная передача данных</h2>
                <p className="body-medium">
                  Ваши данные могут обрабатываться на серверах, расположенных в разных странах. Мы обеспечиваем адекватный уровень защиты данных при любой международной передаче в соответствии с применимым законодательством.
                </p>
              </section>

              <section className="legal-section">
                <h2 className="heading-2">11. Дети</h2>
                <p className="body-medium">
                  Наши услуги не предназначены для лиц младше 18 лет. Мы сознательно не собираем персональные данные детей. Если вы являетесь родителем или опекуном и считаете, что ваш ребенок предоставил нам персональные данные, пожалуйста, свяжитесь с нами.
                </p>
              </section>

              <section className="legal-section">
                <h2 className="heading-2">12. Изменения в Политике конфиденциальности</h2>
                <p className="body-medium">
                  Мы оставляем за собой право вносить изменения в данную Политику конфиденциальности. Обновления будут опубликованы на этой странице с указанием даты последнего изменения. Мы рекомендуем периодически проверять эту страницу для ознакомления с возможными изменениями.
                </p>
              </section>

              <section className="legal-section">
                <h2 className="heading-2">13. Контактная информация</h2>
                <p className="body-medium">
                  Если у вас есть вопросы относительно данной Политики конфиденциальности или вы хотите воспользоваться своими правами, пожалуйста, свяжитесь с нами:
                </p>
                <div className="legal-contact">
                  <p className="body-medium"><strong>Email:</strong> edgekeycommerce@gmail.com</p>
                  <p className="body-medium"><strong>Телефон:</strong> +375 (29) 820-71-89</p>
                </div>
              </section>

              <div className="legal-footer">
                <Link to="/" className="btn-secondary">
                  Вернуться на главную
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicyPage;
